// Feature 012: compare a planned slot's versions and pick one per channel.
// Version cards (text, media, edit) sit above an assign table like the create form's second step:
// each channel row picks a version or "Later", shows a version's length only when it is over that
// network's limit (from the cabinet), and opens its own time on "change". Review sends a preview,
// Confirm schedules. Create mode is VariantGroupCreate; the versions it picked for channels arrive here as
// a preview to confirm.
import React, {useCallback, useEffect, useMemo, useState} from "react";
import Modal from "react-bootstrap/Modal";
import {
    cancelVariantGroup, dropVariantChannel, getPeerActivities, getVariantGroup,
    pickVariants, removeVariant, saveVariant, unassignVariant,
} from "../../../api";
import {
    VariantAssignment, VariantGroupError, VariantGroupType, VariantPickItem,
} from "../../../redux/Content/VariantGroup/types";
import {notifyVariantGroupsChanged} from "../../../components/Calendar/useVariantGroups";
import {measureText, modeNeedsMedia, pickProgress} from "../../../components/Calendar/VariantGroupCard.helpers";
import {getHandlerIconByCode} from "../../../helpers/functions";
import VariantGroupCreate from "./VariantGroupCreate";
import {ChannelOption, LimitFor, MAX_VARIANTS, Translate, VERSION_LABELS, fromLocalInput, toLocalInput} from "./VariantGroup.shared";

export type {ChannelOption} from "./VariantGroup.shared";

interface Props {
    t: Translate;
    /** The group to open, or null with `createAt` set to start a new one. */
    groupUuid: string | null;
    createAt?: Date | null;
    channels: ChannelOption[];
    /** Text limits per channel and kind, for the create form's fit badges. */
    limitFor?: LimitFor;
    onClose: () => void;
    /** Opens a variant post in the full post editor (media and the rest). */
    onOpenInEditor?: (post: any) => void;
}

const errorOf = (e: any): VariantGroupError => (e && typeof e === 'object' ? e : {success: false, message: String(e)});

const VariantGroupModal: React.FC<Props> = ({t, groupUuid, createAt, channels, limitFor, onClose, onOpenInEditor}) => {
    // Create mode has no uuid until the group exists; it then switches to that group.
    const [uuid, setUuid] = useState<string | null>(groupUuid);
    const [group, setGroup] = useState<VariantGroupType | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    // Unsaved choices: channel uuid → label, and channel uuid → its own time.
    const [choice, setChoice] = useState<Record<string, string>>({});
    const [times, setTimes] = useState<Record<string, string>>({});
    const [preview, setPreview] = useState<VariantPickItem[] | null>(null);
    // The request the preview was made for: Confirm sends exactly that.
    const [reviewed, setReviewed] = useState<{ assignments?: VariantAssignment[]; all?: string } | null>(null);
    // Channels whose own-time field is open ("change"), though no new time is typed yet.
    const [timeOpen, setTimeOpen] = useState<Set<string>>(new Set());
    // Channels to publish at once rather than at their time.
    const [nowFor, setNowFor] = useState<Set<string>>(new Set());
    const [editing, setEditing] = useState<{ label: string | null; text: string; tags: string } | null>(null);
    const [confirmCancel, setConfirmCancel] = useState(false);
    // Versions picked for channels while creating: once the group is loaded they become its
    // choices and are previewed, so the person confirms exactly what will be scheduled.
    const [pending, setPending] = useState<VariantAssignment[] | null>(null);

    const load = useCallback((uuid: string) => {
        getVariantGroup(uuid)
            .then((response: any) => {
                setGroup(response.item);
                setChoice({});
                setTimes({});
                setTimeOpen(new Set());
                setNowFor(new Set());
                setPreview(null);
            })
            .catch((e: any) => setError(errorOf(e).message || t("variantGroup:loadFailed", "Could not load this group.")));
    }, [t]);

    useEffect(() => setUuid(groupUuid), [groupUuid]);

    useEffect(() => {
        if (uuid) {
            load(uuid);
        }
    }, [uuid, load]);

    useEffect(() => {
        if (!group || !pending) {
            return;
        }
        setPending(null);
        if (pending.length === 0) {
            return;
        }
        setChoice(Object.fromEntries(pending.map((a) => [a.channel, a.variant])));
        setTimes(Object.fromEntries(pending.filter((a) => a.publishAt).map((a) => [a.channel, toLocalInput(a.publishAt as string)])));
        setBusy(true);
        pickVariants(group.uuid, {preview: true, assignments: pending})
            .then((response: any) => {
                setReviewed({assignments: pending});
                setPreview(response.items || []);
            })
            .catch((e: any) => setError(errorOf(e).message || t("variantGroup:actionFailed", "That did not work.")))
            .finally(() => setBusy(false));
    }, [group, pending, t]);

    const changed = (uuid: string) => {
        notifyVariantGroupsChanged();
        load(uuid);
    };

    const run = (action: () => Promise<any>, then?: (response: any) => void) => {
        setBusy(true);
        setError(null);
        action()
            .then((response: any) => then?.(response))
            .catch((e: any) => setError(errorOf(e).message || t("variantGroup:actionFailed", "That did not work.")))
            .finally(() => setBusy(false));
    };

    const assignments: VariantAssignment[] = useMemo(() => Object.entries(choice).map(([channel, variant]) => (
        nowFor.has(channel)
            ? {channel, variant, now: true}
            : {channel, variant, ...(times[channel] ? {publishAt: fromLocalInput(times[channel])} : {})}
    )), [choice, times, nowFor]);

    if (!uuid) {
        return <VariantGroupCreate t={t} createAt={createAt || new Date()} channels={channels} limitFor={limitFor} onClose={onClose}
                                   onCreated={(created, assignments) => {
                                       notifyVariantGroupsChanged();
                                       setPending(assignments);
                                       setUuid(created.uuid);
                                   }}/>;
    }

    if (!group) {
        return (
            <Modal show onHide={onClose} size="xl" centered>
                <Modal.Body>{error || t("common:loading", "Loading…")}</Modal.Body>
            </Modal>
        );
    }

    const canPick = group.actions.canPick;
    const editable = group.actions.canEdit && group.status !== 'cancelled';
    const {picked, total} = pickProgress(group);
    const unassigned = group.targets.filter((tg) => tg.state === 'unassigned');

    const review = (body: { assignments?: VariantAssignment[]; all?: string }) =>
        run(() => pickVariants(group.uuid, {preview: true, ...body}), (response) => {
            setReviewed(body);
            setPreview(response.items || []);
        });

    const confirm = () => {
        if (!reviewed) {
            return;
        }
        run(() => pickVariants(group.uuid, {preview: false, ...reviewed}), () => changed(group.uuid));
    };

    const previewBlocked = (preview || []).some((item) => item.problems.length > 0);
    const timePassedFor = new Set((preview || []).filter((item) => item.problems.includes('publish_time_passed')).map((item) => item.channel.uuid));

    const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
    const slot = new Date(group.publishAt);
    // A channel's time reads as the slot's time when it is that day, else with its date.
    const timeText = (iso: string) => {
        const d = new Date(iso);
        return sameDay(d, slot)
            ? d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
            : d.toLocaleString([], {weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'});
    };
    const avatarOf = (uuid: string) => channels.find((c) => c.uuid === uuid)?.avatar || null;
    const label = (uuid: string) => choice[uuid] ?? null;
    const pickedCount = assignments.length;

    const choose = (channel: string, value: string | null) => {
        setPreview(null);
        setChoice((prev) => {
            const next = {...prev};
            if (value === null) {
                delete next[channel];
            } else {
                next[channel] = value;
            }
            return next;
        });
    };

    const sameEverywhere = (value: string) => {
        setPreview(null);
        setChoice(Object.fromEntries(unassigned.filter((tg) => tg.channelAvailable).map((tg) => [tg.channel.uuid, value])));
    };

    const openEditor = (variantUuid: string) => onOpenInEditor &&
        getPeerActivities({uuid: variantUuid}).then((r: any) => r?.items?.[0] && onOpenInEditor(r.items[0]));

    const editor = (
        <div className="variantGroupWindow__card variantGroupWindow__card--editing">
            <span className="variantGroupWindow__label">{editing?.label ?? "+"}</span>
            <textarea className="form-control" rows={4} value={editing?.text || ''} aria-label={t("variantGroup:text", "Text")}
                      onChange={(e) => editing && setEditing({...editing, text: e.target.value})}/>
            <input className="form-control" value={editing?.tags || ''} placeholder="#tags" aria-label={t("variantGroup:tags", "Tags")}
                   onChange={(e) => editing && setEditing({...editing, tags: e.target.value})}/>
            <div>
                <button type="button" className="btn btn-primary btn-sm" disabled={busy || !editing || editing.text.trim() === ''} onClick={() => editing && run(
                    () => saveVariant(group.uuid, {...(editing.label ? {label: editing.label} : {}), text: editing.text, tags: editing.tags}),
                    () => {
                        setEditing(null);
                        changed(group.uuid);
                    },
                )}>{t("common:save", "Save")}</button>
                <button type="button" className="btn btn-link btn-sm" onClick={() => setEditing(null)}>{t("common:cancel", "Cancel")}</button>
            </div>
        </div>
    );

    return (
        <Modal show onHide={onClose} size="xl" centered className="variantGroupModal variantGroupWindow">
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2 flex-wrap flex-grow-1">
                    <span>{group.title}</span>
                    {group.mode &&
                        <span className="variantGroupWindow__pill variantGroupWindow__pill--kind">{t("peerActivityMode:" + group.mode.code, group.mode.code)}</span>}
                    <span className={"variantGroupWindow__pill variantGroupWindow__pill--" + (group.overdue ? 'overdue' : group.status)}>
                        {t("variantGroup:state_" + (group.overdue ? 'overdue' : group.status), group.status)}
                    </span>
                    <span className="text-muted fs-13 fw-normal">{slot.toLocaleString([], {weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'})}</span>
                    <span className="variantGroupWindow__progress ms-auto">
                        <span className="text-muted fs-13 fw-normal">{picked}/{total} {t("variantGroup:channelsPicked", "channels picked")}</span>
                        <span className="variantGroupWindow__bar" aria-hidden="true"><span style={{width: (total ? picked / total * 100 : 0) + '%'}}/></span>
                    </span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <div className="alert alert-danger" role="alert">{error}</div>}

                <div className="variantGroupWindow__versions">
                    {group.variants.map((variant) => editing && editing.label === variant.label
                        ? <React.Fragment key={variant.label}>{editor}</React.Fragment>
                        : (
                            <div key={variant.label} className="variantGroupWindow__card" data-testid={`variant-${variant.label}`}>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="variantGroupWindow__label">{variant.label}</span>
                                    <span className="text-muted small flex-grow-1">
                                        {measureText(variant.text, variant.tags)} · {variant.media.length > 0 ? variant.media.length + " " + t("variantGroup:media", "media") : t("variantGroup:noMedia", "no media")}
                                    </span>
                                    {editable &&
                                        <button type="button" className="btn btn-sm btn-ghost-secondary btn-icon" aria-label={t("variantGroup:editVersion", "Edit version") + " " + variant.label}
                                                onClick={() => setEditing({label: variant.label, text: variant.text || '', tags: variant.tags || ''})}>
                                            <i className="ri-pencil-line" aria-hidden="true"/>
                                        </button>}
                                    {editable && group.status === 'open' && group.variants.length > 2 &&
                                        <button type="button" className="btn btn-sm btn-ghost-danger btn-icon" aria-label={t("variantGroup:removeVersion", "Remove version") + " " + variant.label}
                                                onClick={() => run(() => removeVariant(group.uuid, variant.label), () => changed(group.uuid))}>
                                            <i className="ri-delete-bin-line" aria-hidden="true"/>
                                        </button>}
                                </div>
                                <div className="variantGroupWindow__text" title={variant.text || ''}>{variant.text || "—"}</div>
                                {variant.media.length > 0
                                    ? <button type="button" className="variantGroupWindow__thumbs" disabled={!editable || !onOpenInEditor}
                                              aria-label={t("variantGroup:fullEditor", "Media…") + " " + variant.label} onClick={() => openEditor(variant.uuid)}>
                                        {variant.media.slice(0, 4).map((m, i) => (String(m.contentType || '').startsWith('image') && m.url
                                            ? <img key={m.uuid || i} src={m.url} alt=""/>
                                            : <span key={m.uuid || i}><i className="ri-film-line" aria-hidden="true"/></span>))}
                                    </button>
                                    : editable && onOpenInEditor
                                        ? <button type="button" className={"variantGroupWindow__addMedia" + (modeNeedsMedia(group.mode?.code) ? " variantGroupWindow__addMedia--needed" : "")}
                                                  onClick={() => openEditor(variant.uuid)}>
                                            + {modeNeedsMedia(group.mode?.code) ? t("variantGroup:needsMedia", "needs a photo or video") : t("variantGroup:addMedia", "Add photo or video")}
                                        </button>
                                        : modeNeedsMedia(group.mode?.code) &&
                                        <span className="badge bg-warning-subtle text-warning align-self-start">{t("variantGroup:needsMedia", "needs a photo or video")}</span>}
                            </div>
                        ))}
                    {editing && editing.label === null && editor}
                    {editable && group.variants.length < MAX_VARIANTS && !editing &&
                        <button type="button" className="variantGroupWindow__addVersion" onClick={() => setEditing({label: null, text: '', tags: ''})}>
                            + {t("variantGroup:addVersion", "Add version")} {VERSION_LABELS[group.variants.length]}
                        </button>}
                </div>

                <div className="table-responsive variantGroupWindow__tableBox">
                    <table className="table align-middle mb-0 variantGroupWindow__table">
                        <thead>
                        <tr>
                            <th>{t("variantGroup:channel", "Channel")}</th>
                            <th>{t("variantGroup:version", "Version")}</th>
                            <th>{t("variantGroup:time", "Time")}</th>
                            <th aria-label={t("variantGroup:drop", "Skip")}/>
                        </tr>
                        </thead>
                        <tbody>
                        {group.targets.map((target) => {
                            const uuid = target.channel.uuid;
                            const selectable = canPick && target.state === 'unassigned' && target.channelAvailable;
                            const avatar = avatarOf(uuid);
                            const timeShown = timeOpen.has(uuid) || times[uuid] !== undefined || timePassedFor.has(uuid);
                            return (
                                <tr key={uuid} className={"variantGroupWindow__row--" + target.state}>
                                    <td>
                                        <span className="d-flex align-items-center gap-2">
                                            <span className="avatar-xs flex-shrink-0 position-relative variantGroupCreate__avatar">
                                                {avatar ? <img src={avatar} className="rounded-circle img-fluid" alt=""/> : <span className="avatar-title rounded-circle">{target.channel.name[0]}</span>}
                                                <img src={getHandlerIconByCode(target.channel.network)} className="variantGroupCreate__network rounded-circle" alt=""/>
                                            </span>
                                            <span className="mw-0">
                                                <span className="d-block fw-semibold text-truncate">{target.channel.name}</span>
                                                <span className="d-block text-muted small">
                                                    {target.channel.network}
                                                    {!target.channelAvailable && <span className="text-warning"> · {t("variantGroup:unavailable", "Unavailable")}</span>}
                                                    {target.postRemoved && <span className="text-danger"> · {t("variantGroup:postRemoved", "Post removed")}</span>}
                                                </span>
                                            </span>
                                        </span>
                                    </td>
                                    <td>
                                        {target.state === 'assigned' &&
                                            <span className="d-flex align-items-center gap-2 flex-wrap">
                                                <span className="variantGroupWindow__label">{target.variant}</span>
                                                {target.post && <span className="text-muted small">{t("peerActivityStatus:" + target.post.status, target.post.status)}</span>}
                                                {canPick &&
                                                    <button type="button" className="btn btn-link btn-sm p-0" disabled={busy}
                                                            onClick={() => run(() => unassignVariant(group.uuid, uuid), () => changed(group.uuid))}>
                                                        {t("variantGroup:unassign", "Take back")}
                                                    </button>}
                                            </span>}
                                        {target.state === 'dropped' &&
                                            <span className="d-flex align-items-center gap-2">
                                                <span className="text-muted">{t("variantGroup:skipped", "Skipped")}</span>
                                                {editable &&
                                                    <button type="button" className="btn btn-link btn-sm p-0" disabled={busy}
                                                            onClick={() => run(() => dropVariantChannel(group.uuid, uuid, true), () => changed(group.uuid))}>
                                                        {t("variantGroup:restore", "Restore")}
                                                    </button>}
                                            </span>}
                                        {target.state === 'unassigned' &&
                                            <span className="d-flex flex-wrap gap-1" role="group" aria-label={t("variantGroup:version", "Version") + " · " + target.channel.name}>
                                                {group.variants.map((variant) => {
                                                    const limit = target.limits?.[variant.label];
                                                    const on = label(uuid) === variant.label;
                                                    return (
                                                        <button key={variant.label} type="button" aria-pressed={on} disabled={!selectable}
                                                                data-testid={`cell-${uuid}-${variant.label}`}
                                                                title={limit?.over ? `${limit.length}/${limit.maxLength} ${t("variantGroup:overLimit", "over the limit")}` : undefined}
                                                                className={"btn btn-sm variantGroupWindow__pick" + (on ? " btn-primary" : " btn-outline-secondary") + (limit?.over ? " variantGroupWindow__pick--over" : "")}
                                                                onClick={() => choose(uuid, variant.label)}>
                                                            {variant.label}{limit?.over && <span className="variantGroupWindow__over"> · {limit.length}/{limit.maxLength}</span>}
                                                        </button>
                                                    );
                                                })}
                                                <button type="button" aria-pressed={label(uuid) === null} disabled={!selectable}
                                                        className={"btn btn-sm" + (label(uuid) === null ? " btn-secondary" : " btn-outline-secondary")}
                                                        onClick={() => choose(uuid, null)}>
                                                    {t("variantGroup:later", "Later")}
                                                </button>
                                            </span>}
                                    </td>
                                    <td>
                                        {target.state === 'unassigned' && nowFor.has(uuid)
                                            ? <span className="d-flex align-items-center gap-2">
                                                <span className="badge bg-danger-subtle text-danger">{t("variantGroup:publishNowBadge", "Now")}</span>
                                                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => {
                                                    setPreview(null);
                                                    setNowFor((prev) => {
                                                        const next = new Set(prev);
                                                        next.delete(uuid);
                                                        return next;
                                                    });
                                                }}>{t("variantGroup:undo", "undo")}</button>
                                            </span>
                                            : target.state === 'unassigned' && timeShown
                                            ? <span className="d-flex align-items-center gap-2">
                                                <input type="datetime-local"
                                                       className={"form-control form-control-sm" + (timePassedFor.has(uuid) ? " is-invalid" : "")}
                                                       aria-label={t("variantGroup:time", "Time") + " " + target.channel.name}
                                                       value={times[uuid] || toLocalInput(target.effectivePublishAt)}
                                                       disabled={!canPick}
                                                       autoFocus={timePassedFor.has(uuid)}
                                                       onChange={(e) => {
                                                           setPreview(null);
                                                           setTimes((prev) => ({...prev, [uuid]: e.target.value}));
                                                       }}/>
                                                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => {
                                                    setPreview(null);
                                                    setTimes((prev) => {
                                                        const next = {...prev};
                                                        delete next[uuid];
                                                        return next;
                                                    });
                                                    setTimeOpen((prev) => {
                                                        const next = new Set(prev);
                                                        next.delete(uuid);
                                                        return next;
                                                    });
                                                }}>{t("variantGroup:reset", "Reset")}</button>
                                            </span>
                                            : <span className="small">
                                                {timeText(target.effectivePublishAt)}
                                                {target.state === 'unassigned' && canPick &&
                                                    <> · <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                                                                 aria-label={t("variantGroup:changeTime", "Change time") + " · " + target.channel.name}
                                                                 onClick={() => setTimeOpen((prev) => new Set(prev).add(uuid))}>
                                                        {t("variantGroup:change", "change")}
                                                    </button> · <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                                                                        aria-label={t("variantGroup:publishNow", "Publish now") + " · " + target.channel.name}
                                                                        onClick={() => {
                                                                            setPreview(null);
                                                                            setNowFor((prev) => new Set(prev).add(uuid));
                                                                        }}>
                                                        {t("variantGroup:nowLink", "now")}
                                                    </button></>}
                                            </span>}
                                        {timePassedFor.has(uuid) && <div className="invalid-feedback d-block">{t("variantGroup:timePassed", "This time has passed — choose a new one")}</div>}
                                    </td>
                                    <td className="text-end">
                                        {editable && target.state === 'unassigned' &&
                                            <button type="button" className="btn btn-sm btn-ghost-secondary btn-icon" disabled={busy}
                                                    aria-label={t("variantGroup:drop", "Skip") + " " + target.channel.name}
                                                    title={t("variantGroup:drop", "Skip")}
                                                    onClick={() => run(() => dropVariantChannel(group.uuid, uuid), () => changed(group.uuid))}>
                                                <i className="ri-forbid-line" aria-hidden="true"/>
                                            </button>}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>

                {preview &&
                    <div className="variantGroupModal__preview mt-3" data-testid="pick-preview">
                        <h6>{t("variantGroup:reviewTitle", "This will be scheduled")}</h6>
                        <ul className="list-unstyled mb-0">
                            {preview.map((item) => (
                                <li key={item.channel.uuid} className={"variantGroupModal__previewItem" + (item.problems.length ? " text-danger" : "")}>
                                    <strong>{item.channel.name}</strong> · {item.variant} · {item.now
                                        ? <span className="badge bg-danger-subtle text-danger">{t("variantGroup:publishNowBadge", "Now")}</span>
                                        : new Date(item.effectivePublishAt).toLocaleString()}
                                    {item.over && <span className="badge bg-warning text-dark ms-2">{item.length}/{item.maxLength} {t("variantGroup:overLimit", "over the limit")}</span>}
                                    {item.problems.map((p) => <span key={p} className="badge bg-danger ms-2">{t("variantGroup:problem_" + p, p)}</span>)}
                                    <div className="small text-muted text-truncate">{item.text}</div>
                                </li>
                            ))}
                        </ul>
                    </div>}

                {confirmCancel &&
                    <div className="alert alert-warning mt-3" role="alert">
                        {t("variantGroup:cancelConfirm", "Cancel this group? No more versions will be scheduled from it.")}
                        {group.targets.some((tg) => tg.state === 'assigned') &&
                            <div className="mt-1">{t("variantGroup:cancelKeeps", "These stay scheduled:")} {group.targets.filter((tg) => tg.state === 'assigned').map((tg) => tg.channel.name).join(", ")}</div>}
                        <div className="mt-2">
                            <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => run(() => cancelVariantGroup(group.uuid), () => {
                                setConfirmCancel(false);
                                changed(group.uuid);
                            })}>{t("variantGroup:cancelGroup", "Cancel group")}</button>
                            <button type="button" className="btn btn-link btn-sm" onClick={() => setConfirmCancel(false)}>{t("common:back", "Back")}</button>
                        </div>
                    </div>}
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <div>
                    {editable && group.status !== 'cancelled' && !confirmCancel &&
                        <button type="button" className="btn btn-link text-danger px-0" onClick={() => setConfirmCancel(true)}>{t("variantGroup:cancelGroup", "Cancel group")}</button>}
                </div>
                {canPick && unassigned.length > 0 &&
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="text-muted small">{t("variantGroup:sameEverywhere", "Same version everywhere")}:</span>
                        {group.variants.map((v) => (
                            <button key={v.label} type="button" className="btn btn-sm btn-outline-primary" disabled={busy} onClick={() => sameEverywhere(v.label)}>{v.label}</button>
                        ))}
                        {!preview &&
                            <button type="button" className="btn btn-soft-danger ms-2" disabled={busy || pickedCount === 0}
                                    onClick={() => review({assignments: assignments.map((a) => ({channel: a.channel, variant: a.variant, now: true}))})}>
                                {t("variantGroup:publishNow", "Publish now")}{pickedCount > 0 && ` · ${pickedCount}`}
                            </button>}
                        {!preview &&
                            <button type="button" className="btn btn-primary" disabled={busy || pickedCount === 0} onClick={() => review({assignments})}>
                                {t("variantGroup:reviewPicks", "Review")}{pickedCount > 0 && ` · ${pickedCount}`}
                            </button>}
                        {preview &&
                            <button type="button" className="btn btn-link" disabled={busy} onClick={() => setPreview(null)}>{t("variantGroup:back", "Back")}</button>}
                        {preview &&
                            <button type="button" className={"btn " + (preview.length > 0 && preview.every((item) => item.now) ? "btn-danger" : "btn-success")}
                                    disabled={busy || previewBlocked || preview.length === 0} onClick={confirm}>
                                {preview.length > 0 && preview.every((item) => item.now)
                                    ? t("variantGroup:confirmPublishNow", "Publish now")
                                    : t("variantGroup:confirm", "Confirm")}
                            </button>}
                    </div>}
            </Modal.Footer>
        </Modal>
    );
};


export default VariantGroupModal;
